import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

type BlogPost = {
  title: string;
  date: string;
  category: string;
  summary: string;
  content: string;
  cta?: {
    label: string;
    href: string;
  };
};

const posts: BlogPost[] = [
  {
    title: 'Launching TrendBeat for Fashion & Beauty Teams',
    date: 'February 2025',
    category: 'Product',
    summary: 'Why we built TrendBeat and how it augments existing research processes.',
    content:
      'TrendBeat started inside TwoKai as an internal tool for fashion and beauty clients that needed dependable social listening without a research department. We merged our AI playbooks with daily crawls, layered LLM summaries over every dataset, and exposed it all through a simple API plus dashboard. The result is a single source of truth for engagement scores, viral potential, and growth trajectories that marketing and merchandising teams can reference before committing to production runs or paid campaigns.',
    cta: {
      label: 'See the platform',
      href: '/signup',
    },
  },
  {
    title: 'How We Score Emerging Trends',
    date: 'March 2025',
    category: 'Insights',
    summary: 'A quick look at the metrics that appear throughout the dashboard and reports.',
    content:
      'Every trend we surface carries the same quantitative profile you see across the app: engagement score, growth rate, trend strength, viral potential, momentum score, and post counts. These metrics are derived from live social signals pulled from multiple platforms, enriched with anomaly detection, and normalised so teams can compare beauty and fashion stories side by side. We intentionally keep the presentation simple—clear colour cues, concise summaries generated by our LLM layer, and exportable data so analysts can run their own models.',
  },
  {
    title: 'Using the TrendBeat API in Product Planning',
    date: 'March 2025',
    category: 'API',
    summary: 'Connecting programmatic access to the same data that powers our dashboard.',
    content:
      'The TrendBeat API mirrors what is available inside the dashboard, including authentication, rate limiting, and the same forecasting datasets. Teams typically start by hitting the /trends endpoints a few hundred times per month to feed planning spreadsheets, while more advanced users integrate alerts straight into PLM tools or email digests. Because the API is part of every paid plan, developers do not need extra contracts—just request a key, plug it into the docs examples, and automate the trend checks that usually happen manually.',
    cta: {
      label: 'Read the docs',
      href: '/docs',
    },
  },
];

const BlogPage: React.FC = () => {
  return (
    <div className="bg-white">
      <section className="py-20 bg-gradient-to-br from-purple-50 to-indigo-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          >
            TrendBeat Blog
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-lg text-gray-600 max-w-3xl mx-auto"
          >
            Short updates on the platform, the data powering it, and how teams deploy TrendBeat in real fashion and beauty workflows.
          </motion.p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          {posts.map((post, index) => (
            <motion.article
              key={post.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="bg-white border border-gray-200 rounded-2xl shadow-sm p-8"
            >
              <div className="flex items-center text-sm text-gray-500 space-x-3 mb-4">
                <span className="uppercase tracking-wide font-semibold text-purple-600">
                  {post.category}
                </span>
                <span>•</span>
                <span>{post.date}</span>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">{post.title}</h2>
              <p className="text-lg text-gray-600 mb-4">{post.summary}</p>
              <p className="text-gray-700 leading-relaxed mb-6">{post.content}</p>
              {post.cta && (
                <Link
                  to={post.cta.href}
                  className="text-purple-600 font-semibold hover:text-purple-700 inline-flex items-center"
                >
                  {post.cta.label}
                  <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              )}
            </motion.article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default BlogPage;

